import React, {
    createContext,
    useContext,
    useState
} from 'react';
import { useToast } from '@chakra-ui/react';
import axiosInstance from '../../../../../api/axiosInstance';

interface OptionType {
    label: string;
    value: string;
};

interface IState {
    state: {
        btnFilterSelected: string | null,
        page: number;
        perPage: number;
        totalPage: number;
        keyword: string;
        isLoading: boolean;
        url: string;
        listData: [];
        isOpenModalPemateri: boolean;
        jenisPemateriSelected: OptionType | null;
        isOpenModalDeletePemateri: boolean;
        nip: number | null;
        isLoadingNIP: boolean;
        dataAsn: any;
        isLoadingSubmit: boolean;
        nama: string;
        nik: number | null;
        tempat_lahir: string;
        tanggal_lahir: string;
        jabatan: string;
        instansi: string;
        isEdit: boolean;
        dataEdit: any;
        idUpdate: number | null;
        idDelete: number | null;
        errorMessage: string; 
    };
    dispatch: {
        setPemateriState: Function;
        getDataPemateri: Function;
        handleCheckNIP: Function;
        handleSubmit: Function;
        clearState: Function;
        handleDelete: Function;
    };
}

const initialState: IState = {
    state: {
        btnFilterSelected: null,
        page: 1,
        perPage: 10,
        totalPage: 0,
        keyword: '',
        isLoading: false,
        url: '/pemateri',
        listData: [],
        isOpenModalPemateri: false,
        jenisPemateriSelected: null,
        isOpenModalDeletePemateri: false,
        nip: null,
        isLoadingNIP: false,
        dataAsn: {},
        isLoadingSubmit: false,
        nama: '',
        nik: null,
        tempat_lahir: '',
        tanggal_lahir: '',
        jabatan: '',
        instansi: '',
        isEdit: false,
        dataEdit: null,
        idUpdate: null,
        idDelete: null,
        errorMessage: '',
    },
    dispatch: {
        setPemateriState: () => { },
        getDataPemateri: () => { },
        handleCheckNIP: () => { },
        handleSubmit: () => { },
        clearState: () => { },
        handleDelete: () => { },
    },
};

const Context = createContext<IState>(initialState);
const { Provider: PemateriProvider } = Context;

const Provider: React.FC = ({ children }) => {
    const [state, setState] = useState<IState>(initialState);
    const toast = useToast();

    const {
        page,
        perPage,
        keyword,
        url,
        btnFilterSelected,
        nip,
        jenisPemateriSelected,
        nama,
        nik,
        tempat_lahir,
        tanggal_lahir,
        jabatan,
        instansi,
        isEdit,
        idUpdate,
        idDelete,
    } = state.state;

    const setPemateriState = (
        key: keyof IState['state'],
        value: any
    ) => {
        setState((prevState) => ({
            ...prevState,
            state: {
                ...prevState.state,
                [key]: value,
            },
        }));
    };

    const getDataPemateri = async () => {
        setPemateriState('isLoading', true);
        try {
            const response = await axiosInstance.get(url, {
                params: {
                    page,
                    per_page: perPage,
                    search: keyword,
                    jenis: btnFilterSelected,
                },
            });

            setPemateriState('listData', response.data?.data);
            setPemateriState('totalPage', response.data?.total);
        } catch (error) {
            toast({
                title: 'Error',
                description: 'Ada Kesalahan Saat Mengambil Data!',
                status: 'error',
                position: 'top-right',
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setPemateriState('isLoading', false);
        }
    }

    const validateForm = () => {
        const validationErrors: string[] = [];

        if (!jenisPemateriSelected) {
            validationErrors.push('Jenis pemateri harus dipilih.');
        }

        if (jenisPemateriSelected?.value === 'ASN' && !nip) {
            validationErrors.push('NIP tidak boleh kosong untuk jenis pemateri ASN.');
        }

        if (!nama) {
            validationErrors.push('Nama tidak boleh kosong.');
        }

        if (!nik && jenisPemateriSelected?.value === 'NON_ASN') {
            validationErrors.push('NIK tidak boleh kosong.');
        }

        if (!tempat_lahir && jenisPemateriSelected?.value !== 'ASN') {
            validationErrors.push('Tempat lahir tidak boleh kosong.');
        }

        if (!tanggal_lahir && jenisPemateriSelected?.value !== 'ASN') {
            validationErrors.push('Tanggal lahir tidak boleh kosong.');
        }

        if (!jabatan) {
            validationErrors.push('Jabatan tidak boleh kosong.');
        }

        if (!instansi) {
            validationErrors.push('Instansi tidak boleh kosong.');
        }

        validationErrors.forEach((message) =>
            toast({
                title: 'Error',
                description: message,
                status: 'error',
                position: 'top-right',
                duration: 3000,
                isClosable: true,
            })
        );

        if (validationErrors.length > 0) {
            return false;
        }
        return true;
    }

    const createFormData = () => {
        const formData = new FormData();
        if (jenisPemateriSelected) {
            formData.append('jenis', jenisPemateriSelected.value);
        }
        formData.append('nama', nama);

        if(nik && jenisPemateriSelected?.value === 'NON_ASN'){
            formData.append('nik', nik.toString());
        }

        if (jenisPemateriSelected?.value === 'ASN' && nip) {
            formData.append('nip', nip.toString());
        }

        if (tempat_lahir) formData.append('tempat_lahir', tempat_lahir);
        if (tanggal_lahir) formData.append('tanggal_lahir', tanggal_lahir);
        formData.append('jabatan', jabatan);
        formData.append('instansi', instansi);

        if (isEdit) {
            formData.append('_method', 'PUT');
        }

        return formData;
    }

    const handleSubmit = async () => {
        setPemateriState('isLoadingSubmit', true);

        const validate = validateForm();
        if (!validate) {
            setPemateriState('isLoadingSubmit', false);
            return;
        }


        const formData = createFormData();

        try {
            let response;

            if (isEdit) {
                response = await axiosInstance.post(`${url}/${idUpdate}`, formData);
            } else {
                response = await axiosInstance.post(`${url}`, formData);
            }

            if (response.status === 200) {
                setPemateriState('isOpenModalPemateri', false);
                getDataPemateri();
                clearState();
                toast({
                    title: response?.data?.message ?? 'Data pemateri berhasil disimpan.',
                    status: 'success',
                    position: 'top-right',
                    duration: 3000,
                    isClosable: true,
                });
            }
        } catch (error) {
            const errors = error.response?.data?.errors;
            const message = errors
            ? Object.values(errors).flat().join('\n')
            : 'Terjadi kesalahan saat mengirim data';

            toast({
                title: 'Error',
                description: message,
                status: 'error',
                position: 'top-right',
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setPemateriState('isLoadingSubmit', false);
        }
    }

    const handleDelete = async () => {
        setPemateriState('isLoadingSubmit', true);

        try {
            const response = await axiosInstance.delete(`${url}/${idDelete}`);

            if (response.status === 200) {
                setPemateriState('isOpenModalDeletePemateri', false);
                getDataPemateri();
                setPemateriState('idDelete', null);
                toast({
                    title: response?.data?.message ?? 'Data pemateri berhasil dihapus.',
                    status: 'success',
                    position: 'top-right',
                    duration: 3000,
                    isClosable: true,
                });
            }
        } catch (error) {
            const titlle = error.response?.data?.message || 'Terjadi kesalahan saat mengirim data'
            toast({
                title: 'Error',
                description: titlle,
                status: 'error',
                position: 'top-right',
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setPemateriState('isLoadingSubmit', false);
        }
    }

    const clearState = () => {
        setPemateriState('jenisPemateriSelected', null);
        setPemateriState('nama', '');
        setPemateriState('nik', null);
        setPemateriState('nip', null);
        setPemateriState('tempat_lahir', '');
        setPemateriState('tanggal_lahir', '');
        setPemateriState('jabatan', '');
        setPemateriState('instansi', '');
        setPemateriState('dataAsn', {});
        setPemateriState('isEdit', false);
        setPemateriState('dataEdit', null);
    };

    const handleCheckNIP = async () => {
        setPemateriState('isLoadingNIP', true);
        setPemateriState('errorMessage', '');

        try {
            if (!nip) {
                toast({
                    title: 'Warning',
                    description: 'NIP tidak boleh kosong!',
                    status: 'warning',
                    position: 'top-right',
                    duration: 3000,
                    isClosable: true,
                });
                return;
            }

            const response = await axiosInstance.post(`/cek_nip`, {
                nip: nip,
            });

            if (response.status === 200) {
                setPemateriState('dataAsn', response.data?.data);
                setPemateriState('nama', response.data?.data.NAMA_LENGKAP);
                setPemateriState('nik', response.data?.data.nik);
                setPemateriState('tempat_lahir', response.data?.data.TEMPAT_LAHIR);
                setPemateriState('tanggal_lahir', response.data?.data.TANGGAL_LAHIR);
                toast({
                    title: 'Berhasil! NIP ditemukan',
                    status: 'success',
                    position: 'top-right',
                    duration: 3000,
                    isClosable: true,
                });
            }
        } catch (error) {
            const title = error.response?.data?.message ||
                'Terjadi kesalahan saat mengambil data';

            toast({
                title: 'Error',
                description: title,
                status: 'error',
                position: 'top-right',
                duration: 3000,
                isClosable: true,
            });

            setPemateriState('errorMessage', title);
            setPemateriState('dataAsn', {})
        } finally {
            setPemateriState('isLoadingNIP', false);
        }
    }

    return (
        <PemateriProvider
            value={{
                state: state.state,
                dispatch: {
                    setPemateriState,
                    getDataPemateri,
                    handleCheckNIP,
                    handleSubmit,
                    clearState,
                    handleDelete,
                },
            }}
        >
            {children}
        </PemateriProvider>
    );
}

export const usePemateriContext = () => useContext(Context);

const PemateriContext = {
    usePemateriContext,
    Provider,
};

export default PemateriContext;